import axios from 'axios';
import config from '../config';

interface LoginCredentials {
  username: string;
  password: string;
  companyCode: string;
}

export interface StoredUser {
  userId: number;
  username: string;
  name: string;
  email: string;
  companyCode: string;
  companyId: number;
  isAdmin: boolean;
  logoPath: string;
  mode: number;
  scopes: string[];
  roles: Array<{
    id: string;
    name: string;
  }>;
  registeredModules: Array<{
    id: number;
    name: string;
  }>;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  companyCode: string;
  companyId: number;
  isAdmin: boolean;
  logoPath: string;
  mode: number;
  scopes: string[];
  registeredModules: Array<{
    id: number;
    name: string;
  }>;
  roles: Array<{
    id: string;
    name: string;
  }>;
  userId: number;
  username: string;
  name?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
}

interface ApiError {
  message: string;
  status: string;
  statusCode: number;
}

function formatUsernameForDisplay(username: string): string {
  if (!username) return 'User';

  const value = username.includes('@') ? username.split('@')[0] : username;
  return value
    .replace(/[._-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeUserData(data: Partial<LoginResponse> & Record<string, unknown>): StoredUser {
  const username = String(data.username ?? '');
  const email =
    (typeof data.email === 'string' && data.email) ||
    (username.includes('@') ? username : '');
  const firstName = typeof data.firstName === 'string' ? data.firstName : '';
  const lastName = typeof data.lastName === 'string' ? data.lastName : '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const name =
    (typeof data.name === 'string' && data.name) ||
    (typeof data.displayName === 'string' && data.displayName) ||
    fullName ||
    formatUsernameForDisplay(username);

  return {
    userId: Number(data.userId ?? 0),
    username,
    name,
    email,
    companyCode: String(data.companyCode ?? ''),
    companyId: Number(data.companyId ?? 0),
    isAdmin: Boolean(data.isAdmin),
    logoPath: String(data.logoPath ?? ''),
    mode: Number(data.mode ?? 0),
    scopes: Array.isArray(data.scopes) ? data.scopes : [],
    roles: Array.isArray(data.roles) ? data.roles : [],
    registeredModules: Array.isArray(data.registeredModules)
      ? data.registeredModules
      : [],
  };
}

class AuthService {
  private baseURL = config.apiBaseUrl;

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(
        `${this.baseURL}/identity/api/v1/auth/authenticate`,
        {
          username: credentials.username,
          password: credentials.password,
          companyCode: credentials.companyCode
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'companyCode': credentials.companyCode
          }
        }
      );

      if (response.data) {
        // Store auth tokens
        localStorage.setItem(config.storageKeys.authToken, response.data.access_token);
        localStorage.setItem(config.storageKeys.refreshToken, response.data.refresh_token);

        // Store user data
        const userData = normalizeUserData(response.data as any);
        localStorage.setItem(config.storageKeys.userData, JSON.stringify(userData));

        // Store company logo path
        if (response.data.logoPath) {
          localStorage.setItem(config.storageKeys.companyLogo, response.data.logoPath);
        }
      }

      return response.data;
    } catch (error: any) {
      // Handle axios error response
      if (error.response) {
        const apiError = error.response.data as ApiError;
        
        // Map status codes to user-friendly messages
        switch (apiError.statusCode) {
          case 404:
            throw new Error('Invalid username or email. Please check your credentials.');
          case 401:
            throw new Error('Invalid password. Please try again.');
          case 403:
            throw new Error('Access denied. Please check your company code.');
          default:
            throw new Error(apiError.message || 'Authentication failed. Please try again.');
        }
      }
      
      // Handle network or other errors
      throw new Error('Connection error. Please check your internet connection.');
    }
  }

  logout(): void {
    // Clear all storage data
    localStorage.clear();
    sessionStorage.clear();
  }

  getCurrentUser(): StoredUser | null {
    const userStr = localStorage.getItem(config.storageKeys.userData);
    if (!userStr) return null;

    try {
      return normalizeUserData(JSON.parse(userStr));
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(config.storageKeys.authToken);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(config.storageKeys.refreshToken);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isAdmin || false;
  }
}

export default new AuthService(); 